<template>
  <div class="context-wrap">
    <div
      class="editor-wrap"
      :class="newContext ? 'new-context' : ''"
    >
      <div class="label-wrap">
        <strong v-if="!newContext && !isEdit">
          {{ localContext }}
        </strong>
        <span @click="openFlowEntityModal">
          <or-icon
            v-if="hasFlowEntity"
            class="fe-label"
            icon="swap_calls"
          />
        </span>
      </div>
      <input
        v-if="newContext || isEdit"
        v-model="localContext"
        ref="context-input"
        class="context-input"
        :placeholder="$t('common.context.context_header.contextInput.placeholder')"
        :maxlength="contextMaxLength"
      />
      <div class="toolbar-wrap">
        <context-header-toolbar
          :new-context="newContext"
          :newPair="newPair"
          :new-context-name="newContextName"
          :context="localContext"
          :context-id="contextId"
          :is-loading="isLoading"
          :is-pattern-view="isPatternView"
          @edit="onEdit"
          @add-pair="$emit('add')"
          @delete="onDelete"
          @confirm="onConfirm"
          @deny="onDeny"
        />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import KnowledgeBus from '@/KnowledgeBus'
import { mapActions } from 'vuex'
import { intentValidationMixin } from '@/mixins/intent-validation.mixin'
import { table_limits as tableLimits } from '@/modules/limits'
import contextHeaderToolbar from './context_header_toolbar'

export default {
  components: { contextHeaderToolbar },

  mixins: [intentValidationMixin],

  props: {
    context: String,
    contextId: String,
    newContext: Boolean,
    newPair: Object,
    hasFlowEntity: Boolean,
    isPatternView: Boolean,
    localIntent: Object
  },

  computed: {
    localContext: {
      get () {
        return this.newContext ? this.newContextName : this.context
      },
      set (value) {
        if (this.newContext) {
          this.newContextName = value
        }
        this.$emit('update:context', value)
      }
    },

    contextMaxLength () {
      return tableLimits.context.max
    },

    showContextLabel () {
      return this.newContext ? false : this.isEdit
    }
  },

  data () {
    return {
      isEdit: false,
      isLoading: false,
      valueOnEdit: String,
      newContextName: ''
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['updateContext', 'createPair']),
    ...mapActions('knowledgebase/patterns', ['createPatternPair']),
    ...mapActions('knowledgebase/notifications', ['addInfo']),

    onEdit () {
      this.isEdit = true
      this.valueOnEdit = _.cloneDeep(this.localContext)
      this.newContextName = _.clone(this.localContext)
      this.$nextTick(() => {
        if (this.$refs['context-input']) {
          this.$refs['context-input'].focus()
        }
      })
    },

    onDelete () {
      this.$emit('delete')
    },

    async confirmAdd (context) {
      this.$v.localIntent.$touch()
      if (this.$v.localIntent.$invalid) {
        return
      }
      if (this.isPatternView) {
        await this.createPatternPair({
          applicationName: context,
          intentName: this.newPair.intent.name,
          intentType: this.newPair.intent.type,
          patternText: this.newPair.pattern.pattern,
          patternEntities: this.newPair.pattern.entities
        })
      } else {
        const pair = await this.createPair({
          applicationName: context,
          intentName: _.trim(this.newPair.intent.name),
          intentType: this.newPair.intent.type,
          utteranceText: _.trim(this.newPair.utterance),
          customData: this.newPair.intent.customData
        })
        if (pair) {
          this.emitRecommendationsNotification(pair)
        }
      }
      this.isEdit = false

      KnowledgeBus.$emit('pair-form::refresh')

      // this.$v.localIntent.$reset()

      KnowledgeBus.$emit('context-form::hide')
      this.$emit('hide-pair-form')
    },

    emitRecommendationsNotification (utterance) {
      this.addInfo({
        titleLabel: this.$t('common.utterance.utterance.recommendations.notifications.pairAdded.label'),
        event: {
          label: this.$t('common.utterance.utterance.recommendations.notifications.pairAdded.eventLabel'),
          id: 'phrases-recommendations::show',
          param: utterance
        }
      })
    },

    async onConfirm (action) {
      this.isLoading = true
      switch (action) {
        case 'rename':
          this.isEdit = false
          await this.updateContext({
            id: this.contextId,
            name: this.localContext
          })
          break
        case 'add':
          await this.confirmAdd(this.context)
          this.isEdit = false
          break
        case 'new':
          await this.confirmAdd(this.newContextName)
          break
      }
      this.isLoading = false
    },

    onDeny (action) {
      if (action === 'edit') {
        this.localContext = _.cloneDeep(this.valueOnEdit)
        this.isEdit = false
      }
      if (action === 'add') {
        this.$emit('hide-pair-form')
      }
    },

    openFlowEntityModal () {
      KnowledgeBus.$emit('context-group-flow-entity::append', this.contextId)
    }
  }
}
</script>

<style lang="scss" scoped>
.context-wrap {
  display: flex;
  justify-content: space-between;
  background-color: $or-grey-light;
  border-top: solid 1px $or-grey;

  .editor-wrap {
    padding-left: $sp-2;
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
    align-items: center;
    margin: 0;
    color: $or-black-blue;
    font-size: 14px;
    line-height: 16px;
    border-left: solid 1px $or-grey;
    border-right: solid 1px $or-grey;
    border-bottom: solid 1px $or-grey;

    .label-wrap {
      display: flex;
      align-items: center;

      .fe-label {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        width: 20px;
        height: 20px;
        margin-left: $sp-2;
        border-radius: 4px;
        background: $or-grey;
        color: $or-white;

        &.problem {
          background-color: $or-yellow;
        }

        @keyframes removing {
          0% {
            background-color: $or-red;
          }
          50% {
            background-color: lighten($or-red, 15%);
          }
          100% {
            background-color: $or-red;
          }
        }

        @keyframes appending {
          0% {
            background-color: $or-grey;
          }
          50% {
            background-color: $or-grey-light;
          }
          100% {
            background-color: $or-grey;
          }
        }

        &.remove {
          animation: removing infinite;
          animation-duration: 0.5s;
        }

        &.append {
          animation: appending infinite;
          animation-duration: 0.5s;
        }

        &:hover {
          background: $or-grey-dark;
          cursor: pointer;
          transition: all 0.1s ease;
        }
      }
    }

    &.new-context {
      padding-left: 6px;
    }

    .toolbar-wrap {
      display: flex;
      justify-content: flex-end;
    }

    .context-input {
      display: flex;
      flex-grow: 1;
      padding-left: 3px;
      margin-top: -3px;
      line-height: 30px;
      font-weight: bold;
      color: $or-black-blue;
      height: 32px;
      overflow: hidden;
      outline: none;
      resize: none;
      border: none;
      background: transparent;

      &::placeholder {
        color: $or-grey-dark;
      }
    }
  }
}
</style>

<style lang="scss">
.editor-wrap {
  .ui-textbox {
    margin-bottom: 0;
    flex-grow: 1;
  }
}
</style>
